import axios from 'axios';
import config from '../config';

/**
 * Service for managing user profile operations
 */
const profileService = {
  /**
   * Get the user's complete profile information
   * @returns {Promise} - User profile data
   */
  getProfile: async () => {
    try {
      // Get the authentication token
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = `_t=${Date.now()}&_r=${Math.random()}`;
      
      console.log('🔒 SECURITY: Fetching fresh profile data with cache-busting');
      const response = await axios.get(
        `${config.API_URL}/users/profile?${cacheBuster}`,
        { headers }
      );
      
      console.log('🔒 SECURITY: Profile API response:', response.data?.data?.email);
      return response.data;
    } catch (error) {
      console.error('Error loading user profile:', error);
      throw error;
    }
  },

  /**
   * Update the user's profile information
   * @param {Object} profileData - The profile data to update
   * @returns {Promise} - API response
   */
  updateProfile: async (profileData) => {
    try {
      // Get the authentication token
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const response = await axios.put(
        `${config.API_URL}/users/profile`,
        profileData,
        { headers }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  },

  /**
   * Upload a profile image
   * @param {File} imageFile - The image file to upload
   * @returns {Promise} - API response with image URL
   */
  uploadProfileImage: async (imageFile) => {
    try {
      // Create a FormData object for file upload
      const formData = new FormData();
      formData.append('profileImage', imageFile);
      
      // Get the authentication token
      const token = localStorage.getItem('token');
      const headers = { 
        'Authorization': `Bearer ${token}`
        // Don't set Content-Type manually - let axios handle it for FormData
      };
      
      const response = await axios.post(
        `${config.API_URL}/users/profile-image`,
        formData,
        { headers }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error uploading profile image:', error);
      throw error;
    }
  },

  /**
   * Update user's email address
   * @param {Object} data - Contains new email and password for verification
   * @returns {Promise} - API response
   */
  updateEmail: async (data) => {
    try {
      // Get the authentication token
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const response = await axios.put(
        `${config.API_URL}/auth/update-email`,
        data,
        { headers }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error updating email:', error);
      throw error;
    }
  },

  /**
   * Deactivate user account (soft delete)
   * @param {Object} data - Contains confirmEmail and optional reason
   * @returns {Promise} - API response
   */
  deactivateAccount: async (data) => {
    try {
      // Get the authentication token
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const response = await axios.post(
        `${config.API_URL}/users/deactivate`,
        data,
        { headers }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error deactivating account:', error);
      throw error;
    }
  },

  /**
   * Reactivate user account
   * @returns {Promise} - API response
   */
  reactivateAccount: async () => {
    try {
      // Get the authentication token
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const response = await axios.post(
        `${config.API_URL}/users/reactivate`,
        {},
        { headers }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error reactivating account:', error);
      throw error;
    }
  }
};

export default profileService;